import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef, type ReactElement } from "react";
import type { MarketplaceAccountView } from "@cx/contracts";
import { MARKETPLACE_LABELS, isChannelProvider, marketplaceIn } from "@cx/domain";
import { ContentHeader, useToast } from "@cx/ui";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";

/**
 * Volta da autorização de um canal (T3.3): posta a query INTEIRA do redirect uma vez só (a Shopify assina a query
 * toda com `hmac`) e volta para os canais. O servidor confere o state (sessão, empresa, canal, validade), a assinatura
 * e a loja; repetir com o mesmo state devolve a mesma conta.
 */
export function ChannelCallbackPage(): ReactElement {
  const { api } = useSession();
  const { provider } = useParams({ from: "/integracoes/canais/$provider/retorno" });
  const navigate = useNavigate();
  const toast = useToast();
  const sent = useRef(false);
  const query = typeof window === "undefined" ? "" : window.location.search.replace(/^\?/, "");
  const params = new URLSearchParams(query);
  const valid = isChannelProvider(provider);
  const label = valid ? MARKETPLACE_LABELS[provider] : "canal";
  const finish = useMutation({
    mutationFn: () =>
      api<MarketplaceAccountView>(`/marketplaces/channels/${provider}/callback`, {
        method: "POST",
        body: JSON.stringify({ query }),
      }),
    onSuccess: (a) => {
      toast("success", `${a.nickname} conectada`);
      void navigate({ to: "/preferencias/integracoes/canais", replace: true });
    },
  });
  const ok = valid && !!params.get("state") && !params.get("error") && query.length <= 2000;
  useEffect(() => {
    if (!ok || sent.current) return;
    sent.current = true;
    finish.mutate();
  }, [ok, finish]);
  return (
    <>
      <ContentHeader
        title={`Conectando ${valid ? marketplaceIn(provider) : "ao canal"}`}
        breadcrumb={["Preferências", "Integrações", "Canais", label]}
      />
      <div className="box card p-3">
        {!ok && (
          <p className="mb-2">
            {params.get("error")
              ? `A autorização foi recusada ${valid ? marketplaceIn(provider) : "no canal"}.`
              : `Retorno ${valid ? `de ${label}` : "do canal"} incompleto.`}
          </p>
        )}
        {ok && finish.isPending && <p className="mb-0">Concluindo a conexão…</p>}
        {finish.isError && <div className="alert alert-danger py-2">{errorMessage(finish.error)}</div>}
        {(!ok || finish.isError) && <Link to="/preferencias/integracoes/canais">Voltar para os canais</Link>}
      </div>
    </>
  );
}
